// pages/secure/MyCommissionsPage.js
// An agent's own commission statement, in their virtual office. Shows YTD paid,
// pending, and every disbursement — and prints as a clean statement.

import React, { useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../../components/shared/DataList';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));
const STATUS = { draft: 'bg-slate-100 text-slate-600', approved: 'bg-blue-100 text-blue-700', paid: 'bg-green-100 text-green-700' };

const MyCommissionsPage = () => {
    const apiClient = useApiClient();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        apiClient.get('/api/commissions/mine')
            .then(setData)
            .catch(err => setError(err.message || 'Failed to load your commissions.'));
    }, [apiClient]);

    if (error) return <div className="p-6"><p className="text-red-500">{error}</p></div>;
    if (!data) return <div className="p-6"><p>Loading your commission statement...</p></div>;

    return (
        <div className="p-6 max-w-4xl">
            {/* Print styles: hide nav chrome, show only the statement */}
            <style>{`@media print {
                aside, .no-print { display: none !important; }
                body { background: white; }
                .print-area { box-shadow: none !important; }
            }`}</style>

            <div className="flex items-center justify-between mb-4 no-print">
                <h1 className="text-2xl font-bold text-slate-800">My Commissions</h1>
                <button onClick={() => window.print()} className="bg-slate-700 text-white px-4 py-2 rounded-md hover:bg-slate-800">🖨 Print statement</button>
            </div>

            <div className="print-area bg-white rounded-lg shadow p-6">
                <div className="hidden print:block mb-4">
                    <h2 className="text-xl font-bold">Business Real Estate Corp. — Commission Statement</h2>
                    <p className="text-sm text-slate-500">Generated {new Date().toLocaleDateString()}</p>
                </div>

                {/* Summary tiles */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="border rounded-lg p-4 text-center">
                        <p className="text-xs uppercase text-slate-500">Paid in {data.year}</p>
                        <p className="text-2xl font-bold text-green-600">{fmt(data.ytd_paid)}</p>
                    </div>
                    <div className="border rounded-lg p-4 text-center">
                        <p className="text-xs uppercase text-slate-500">Pending</p>
                        <p className="text-2xl font-bold text-amber-600">{fmt(data.pending)}</p>
                    </div>
                    <div className="border rounded-lg p-4 text-center">
                        <p className="text-xs uppercase text-slate-500">All-time paid</p>
                        <p className="text-2xl font-bold text-slate-700">{fmt(data.total_paid)}</p>
                    </div>
                </div>

                <DataList rows={data.disbursements} rowKey="disbursement_id" csvName="my-commissions" defaultSort={{ key: 'paid_date', dir: 'desc' }} empty="No commissions yet."
                    filters={[{ key: 'status', label: 'Status' }]}
                    columns={[
                        { key: 'property', label: 'Property', value: (d) => d.property_address || `Transaction #${d.transaction_id}`,
                            render: (d) => <span className="text-slate-700">{d.property_address || `Transaction #${d.transaction_id}`}</span> },
                        { key: 'paid_date', label: 'Paid', type: 'date' },
                        { key: 'check_number', label: 'Check #' },
                        { key: 'final_price', label: 'Sale price', type: 'number', render: (d) => fmt(d.final_price) },
                        { key: 'gross_commission', label: 'Gross comm.', type: 'number', render: (d) => fmt(d.gross_commission) },
                        { key: 'split_percentage', label: 'My split', type: 'number', value: (d) => Number(d.split_percentage), render: (d) => `${Number(d.split_percentage)}%` },
                        { key: 'agent_amount', label: 'My payout', type: 'number', render: (d) => <span className="font-medium">{fmt(d.agent_amount)}</span> },
                        { key: 'status', label: 'Status', render: (d) => <span className={`text-xs px-2 py-0.5 rounded ${STATUS[d.status] || ''}`}>{d.status}</span> },
                    ]} />

                <p className="text-xs text-slate-400 mt-6">This statement reflects commissions recorded by the brokerage. Records are retained for 5 years per 68 Ill. Adm. Code 1450.755. Questions? Contact the managing broker.</p>
            </div>
        </div>
    );
};

export default MyCommissionsPage;
